import { useNavigate } from 'react-router-dom'
import type { Preco, Produto, Representada, TabelaPreco } from '../../db/tipos'
import { partesDaEmbalagemNaFicha } from '../../produtos/tituloDoProduto'
import { textoDoPrecoNaFicha, textoDaRegraNaFicha } from '../../produtos/textoDoPrecoNaFicha'
import { rotuloPrecoCaixa, ROTULO_PRECO_AVULSO } from '../../produtos/rotuloDoPreco'
import { textoOuTraco } from '../../produtos/textoOuTraco'
import { IconeCaixa, IconeEtiqueta, IconePredio, IconeMoedas, IconeDocumento } from './IconesDaFicha'

// Acabamento pedido por ela em 23/09 21h, comparando com o print: valores
// GRANDES e em negrito, rotulos pequenos em cinza-esverdeado, cards arejados.
const card = 'bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,.06)] p-4'
const rotulo = 'text-xs text-slate-500'
const valorGrande = 'text-lg font-bold leading-tight text-slate-900'
const valorMedio = 'text-sm font-bold text-slate-900'

// A aba Dados Comerciais no modo de VER (print dela, 23/09). O modo de editar
// continua no formulario de FormProduto.tsx.
export default function AbaComercialVendo(props: {
  produto: Produto
  representada: Representada | undefined
  tabelasComPreco: TabelaPreco[]
  tabelaEfetiva: string
  aoEscolherTabela: (id: string) => void
  precoAtual: Preco | undefined
}) {
  const { produto, representada, tabelasComPreco, tabelaEfetiva, precoAtual } = props
  const navegar = useNavigate()
  const embalagem = partesDaEmbalagemNaFicha(produto)
  return (
    <div className="space-y-3">
      {/* 2/5 x 3/5 (roteiro dela, 24/09): o Fabricante "Mavalerio" quebrava no
          card da direita; a Embalagem cabe em menos, com o icone ao lado do
          rotulo em vez de ocupar uma coluna inteira. */}
      <div className="grid grid-cols-5 gap-3">
        <div className={`${card} col-span-2`}>
          <div className="min-w-0">
            <p className={`${rotulo} flex items-center gap-1`}>
              <span className="text-teal-700 shrink-0"><IconeCaixa tamanho={16} /></span>Embalagem
            </p>
            <p className="text-2xl font-bold leading-tight text-slate-900">{embalagem.principal}</p>
            {embalagem.detalhe && <p className="text-xs text-slate-600 mt-1">{embalagem.detalhe}</p>}
          </div>
        </div>
        {/* p-3 e nao p-4: cada coluna de Linha/Fabricante ganha 8px, o que
            faz "DECORAÇÃO" e "MAVALERIO" caberem inteiros a 360px. */}
        <div className={`${card} col-span-3 !p-3`}>
          <div className="flex gap-2">
            <span className="text-teal-700 shrink-0 mt-0.5"><IconeEtiqueta tamanho={22} /></span>
            <div className="min-w-0">
              <p className={rotulo}>Categoria</p>
              <p className={`${valorMedio} truncate`}>{textoOuTraco(produto.categoria ?? '')}</p>
            </div>
          </div>
          {/* Linha e Fabricante LADO A LADO, com um traco no meio -- como no
              print. Empilhados, o card ficava alto e abria um buraco no card
              da Embalagem do lado. */}
          <dl className="grid grid-cols-2 mt-3 pt-2 border-t border-slate-100 divide-x divide-slate-100">
            <div className="min-w-0 pr-1.5">
              <dt className={rotulo}>Linha</dt>
              <dd className="text-xs font-bold text-slate-900 break-words">{textoOuTraco(produto.linha ?? '')}</dd>
            </div>
            <div className="min-w-0 pl-1.5">
              <dt className={rotulo}>Fabricante</dt>
              <dd className="text-xs font-bold text-slate-900 break-words">{textoOuTraco(produto.fabricante ?? '')}</dd>
            </div>
          </dl>
        </div>
      </div>

      <button
        type="button"
        onClick={() => representada && navegar(`/representadas/${representada.id}`)}
        className={`${card} w-full flex items-center gap-3 text-left`}
      >
        <span className="text-teal-700 shrink-0"><IconePredio tamanho={26} /></span>
        <span className="min-w-0 flex-1">
          <span className={`block ${rotulo}`}>Fornecedora</span>
          <span className={`block ${valorGrande} truncate`}>{representada?.nome ?? '—'}</span>
        </span>
        <span aria-hidden="true" className="text-slate-400 text-2xl">›</span>
      </button>

      <div className="rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50 to-white shadow-[0_1px_3px_rgba(15,23,42,.06)] p-4">
        <p className="flex items-center gap-2 text-base font-bold text-slate-900 mb-2">
          <span className="text-teal-700"><IconeMoedas tamanho={22} /></span>Preço
        </p>
        {tabelasComPreco.length === 0 ? (
          <p className="text-sm text-slate-500">
            Este produto ainda não tem preço em nenhuma tabela desta fornecedora. Toque em Editar para lançar o primeiro preço.
          </p>
        ) : (
          // Medido a 360px (foto de 23/09 21h): em duas colunas lado a lado os
          // rotulos e "sem minimo" quebravam em 2 linhas. Seletor na linha
          // inteira e os 4 numeros numa grade 2x2 -- rotulo pequeno em cima,
          // valor em negrito embaixo -- cabe sem quebrar.
          <div className="space-y-3">
            <div>
              {/* id PROPRIO: o seletor da edicao continua com id="tabela-preco". */}
              <label htmlFor="tabela-preco-vendo" className={`block ${rotulo} mb-1`}>Tabela de preço</label>
              <select
                id="tabela-preco-vendo" value={tabelaEfetiva}
                onChange={(e) => props.aoEscolherTabela(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white text-sm font-medium"
              >
                <option value="">Selecione…</option>
                {tabelasComPreco.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </div>
            {tabelaEfetiva === '' ? (
              <p className="text-sm text-slate-500">Escolha a tabela para ver o preço.</p>
            ) : (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-teal-100 pt-3">
                <div><dt className={rotulo}>{rotuloPrecoCaixa(produto.unidade ?? '')}</dt><dd className="text-lg font-bold text-slate-900 whitespace-nowrap">{textoDoPrecoNaFicha(precoAtual?.preco)}</dd></div>
                <div><dt className={rotulo}>{ROTULO_PRECO_AVULSO}</dt><dd className="text-lg font-bold text-slate-900 whitespace-nowrap">{textoDoPrecoNaFicha(precoAtual?.preco_unidade)}</dd></div>
                <div><dt className={rotulo}>Quant. mínima</dt><dd className="text-sm font-semibold text-slate-800">{textoDaRegraNaFicha(precoAtual?.quantidade_minima, 'sem mínimo')}</dd></div>
                <div><dt className={rotulo}>Quant. máxima</dt><dd className="text-sm font-semibold text-slate-800">{textoDaRegraNaFicha(precoAtual?.quantidade_maxima, 'sem máximo')}</dd></div>
              </dl>
            )}
          </div>
        )}
      </div>

      <div className={card}>
        <p className="flex items-center gap-2 text-base font-bold text-slate-900 mb-2">
          <span className="text-teal-700"><IconeDocumento tamanho={22} /></span>Informações adicionais
        </p>
        <dl className="grid grid-cols-2 gap-3 divide-x divide-slate-100">
          <div className="min-w-0">
            <dt className={rotulo}>Estoque</dt>
            <dd className={valorMedio}>{textoOuTraco(produto.estoque?.toString() ?? '')}</dd>
          </div>
          <div className="min-w-0 pl-3">
            <dt className={rotulo}>Observações</dt>
            <dd className="text-sm text-slate-800 whitespace-pre-line break-words">{textoOuTraco(produto.observacoes ?? '')}</dd>
          </div>
        </dl>
      </div>

      {!produto.ativo && (
        <p className="text-sm text-amber-700">Produto INATIVO — não aparece na busca do pedido.</p>
      )}
    </div>
  )
}
